import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon, WalletIcon } from '@heroicons/react/24/outline'
import { supabase } from '@/lib/supabase'

interface Wallet {
  id: string
  wallet_name: string
  wallet_address: string
  channel_code: string
  channel_name: string
  channel_icon: string
  is_default: boolean
  is_enabled: boolean
  account_holder_name?: string
  bank_name?: string
  bank_branch?: string
  network?: string
  created_at: string
}

interface UserDetail {
  user: {
    id: string
    email: string
    full_name: string
    phone?: string
    avatar_url?: string
    is_active: boolean
    last_login_at?: string
    operator_code?: string
    email_verified: boolean
    phone_verified: boolean
    created_at: string
    updated_at: string
  }
  account: {
    real_balance: number
    bonus_balance: number
    freeze_balance: number
    total_deposit: number
    total_withdraw: number
    total_profit: number
    total_loss: number
  }
  operator?: {
    id: string
    name: string
    operator_code: string
    commission_rate: number
  }
  recent_activities: Array<{
    id: string
    type: string
    amount: number
    description: string
    created_at: string
    status?: string
  }>
  trading_stats: {
    total_orders: number
    open_positions: number
    win_rate: number
    total_volume: number
  }
}

export default function UserDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState<UserDetail | null>(null)
  const [wallets, setWallets] = useState<Wallet[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchUserDetail()
    fetchUserWallets()
  }, [id])

  const fetchUserDetail = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiClient.getUserDetail(id!)
      if (response.success && response.data) {
        setUser(response.data)
      } else {
        setError(response.message || '加载用户详情失败')
      }
    } catch (err) {
      setError('加载用户详情失败')
      console.error('Failed to fetch user detail:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchUserWallets = async () => {
    try {
      const { data, error } = await supabase
        .from('user_wallets')
        .select(`
          id,
          wallet_name,
          wallet_address,
          channel_code,
          is_default,
          is_enabled,
          account_holder_name,
          bank_name,
          bank_branch,
          network,
          created_at,
          payment_channels(channel_name, channel_icon)
        `)
        .eq('owner_id', id)
        .eq('owner_type', 'user')
        .order('created_at', { ascending: false })

      if (error) throw error

      if (data) {
        const walletsWithChannels = data.map((wallet: any) => ({
          id: wallet.id,
          wallet_name: wallet.wallet_name,
          wallet_address: wallet.wallet_address,
          channel_code: wallet.channel_code,
          channel_name: wallet.payment_channels?.channel_name || '',
          channel_icon: wallet.payment_channels?.channel_icon || '',
          is_default: wallet.is_default,
          is_enabled: wallet.is_enabled,
          account_holder_name: wallet.account_holder_name,
          bank_name: wallet.bank_name,
          bank_branch: wallet.bank_branch,
          network: wallet.network,
          created_at: wallet.created_at,
        }))
        setWallets(walletsWithChannels)
      }
    } catch (err) {
      console.error('Failed to fetch user wallets:', err)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
        <CheckCircleIcon className="w-4 h-4" />
        正常
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium">
        <XCircleIcon className="w-4 h-4" />
        冻结
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center space-y-4">
            <div className="loading-spinner w-8 h-8"></div>
            <p className="text-sm text-gray-500">加载中...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="page-content">
        <div className="mb-8">
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            返回用户列表
          </button>
          <h1 className="page-title">用户详情</h1>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="text-center py-8">
              <p className="text-red-600">{error || '用户不存在'}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/users')}
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-3"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          返回用户列表
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.user.full_name || user.user.email}</h1>
            <p className="text-sm text-gray-600 mt-1">用户ID: {user.user.id.substring(0, 8)}</p>
          </div>
          <div>{getStatusBadge(user.user.is_active)}</div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="card">
          <div className="card-header py-3">
            <h2 className="text-base font-semibold">基本信息</h2>
          </div>
          <div className="card-body py-3 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">邮箱</label>
              <p className="text-sm text-gray-900 mt-0.5">{user.user.email}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">姓名</label>
              <p className="text-sm text-gray-900 mt-0.5">{user.user.full_name || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">电话</label>
              <p className="text-sm text-gray-900 mt-0.5">{user.user.phone || '-'}</p>
            </div>
            {user.operator && (
              <div>
                <label className="text-xs font-medium text-gray-600">所属运营商</label>
                <p className="text-sm text-gray-900 mt-0.5">{user.operator.name}</p>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-600">邮箱验证</label>
              <p className="text-sm text-gray-900 mt-0.5">
                {user.user.email_verified ? (
                  <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                    <CheckCircleIcon className="w-3 h-3" />
                    已验证
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-gray-500 text-xs">
                    <XCircleIcon className="w-3 h-3" />
                    未验证
                  </span>
                )}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">电话验证</label>
              <p className="text-sm text-gray-900 mt-0.5">
                {user.user.phone_verified ? (
                  <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                    <CheckCircleIcon className="w-3 h-3" />
                    已验证
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-gray-500 text-xs">
                    <XCircleIcon className="w-3 h-3" />
                    未验证
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="card">
          <div className="card-header py-3">
            <h2 className="text-base font-semibold">账户信息</h2>
          </div>
          <div className="card-body py-3 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">真实余额</label>
              <p className="text-base font-semibold text-gray-900 mt-0.5">
                {formatCurrency(user.account.real_balance)}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">赠金余额</label>
              <p className="text-base font-semibold text-green-600 mt-0.5">
                {formatCurrency(user.account.bonus_balance)}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">冻结余额</label>
              <p className="text-base font-semibold text-gray-900 mt-0.5">
                {formatCurrency(user.account.freeze_balance)}
              </p>
            </div>
            <div className="pt-2 border-t border-gray-200">
              <label className="text-xs font-medium text-gray-600">总余额</label>
              <p className="text-base font-semibold text-primary-600 mt-0.5">
                {formatCurrency(
                  user.account.real_balance + user.account.bonus_balance + user.account.freeze_balance
                )}
              </p>
            </div>
          </div>
        </div>
        
        {/* Time Info */}
        <div className="card">
          <div className="card-header py-3">
            <h2 className="text-base font-semibold">时间信息</h2>
          </div>
          <div className="card-body py-3 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">注册时间</label>
              <p className="text-sm text-gray-900 mt-0.5">{formatDate(user.user.created_at)}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">最后更新</label>
              <p className="text-sm text-gray-900 mt-0.5">{formatDate(user.user.updated_at)}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">最后登录</label>
              <p className="text-sm text-gray-900 mt-0.5">
                {user.user.last_login_at ? formatDate(user.user.last_login_at) : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Wallets Info */}
      <div className="card mb-4">
        <div className="card-header py-3">
          <h2 className="text-base font-semibold">钱包信息</h2>
        </div>
        <div className="card-body py-3">
          {wallets.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {wallets.map((wallet) => (
                <div key={wallet.id} className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{wallet.channel_icon}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{wallet.wallet_name}</div>
                        <div className="text-xs text-gray-500">{wallet.channel_name}</div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {wallet.is_enabled ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          启用
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          禁用
                        </span>
                      )}
                      {wallet.is_default && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          默认
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {wallet.channel_code === 'bank_transfer' && (
                      <>
                        <div className="text-xs text-gray-600">
                          <span className="text-gray-500">账户名:</span> {wallet.account_holder_name}
                        </div>
                        <div className="text-xs text-gray-600">
                          <span className="text-gray-500">银行:</span> {wallet.bank_name}
                        </div>
                        <div className="text-xs text-gray-600 font-mono break-all">
                          <span className="text-gray-500">卡号:</span> {wallet.wallet_address}
                        </div>
                      </>
                    )}
                    {wallet.channel_code === 'okx' && (
                      <>
                        <div className="text-xs text-gray-600 font-mono break-all">
                          <span className="text-gray-500">地址:</span> {wallet.wallet_address}
                        </div>
                        {wallet.network && (
                          <div className="text-xs text-orange-600">
                            <span className="text-gray-500">网络:</span> {wallet.network}
                          </div>
                        )}
                      </>
                    )}
                    {(wallet.channel_code === 'alipay' || wallet.channel_code === 'wechat') && (
                      <div className="text-xs text-gray-600 break-all">
                        <span className="text-gray-500">账户:</span> {wallet.wallet_address}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {formatDate(wallet.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <WalletIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm">暂无钱包</p>
            </div>
          )}
        </div>
      </div>

      {/* Fund Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="card">
          <div className="card-body py-3">
            <p className="text-xs text-gray-600">累计充值</p>
            <p className="text-xl font-bold text-gray-900 mt-1">
              {formatCurrency(user.account.total_deposit)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <p className="text-xs text-gray-600">累计提现</p>
            <p className="text-xl font-bold text-gray-900 mt-1">
              {formatCurrency(user.account.total_withdraw)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <p className="text-xs text-gray-600">总盈利</p>
            <p className="text-xl font-bold text-green-600 mt-1">
              {formatCurrency(user.account.total_profit)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <p className="text-xs text-gray-600">总亏损</p>
            <p className="text-xl font-bold text-red-600 mt-1">
              {formatCurrency(user.account.total_loss)}
            </p>
          </div>
        </div>
      </div>

      {/* Trading Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="card">
          <div className="card-header py-3">
            <h2 className="text-base font-semibold">交易统计</h2>
          </div>
          <div className="card-body py-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">总订单数</span>
              <span className="text-lg font-bold text-gray-900">{user.trading_stats.total_orders}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">持仓数</span>
              <span className="text-lg font-bold text-gray-900">{user.trading_stats.open_positions}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">胜率</span>
              <span className="text-lg font-bold text-gray-900">{(user.trading_stats.win_rate * 100).toFixed(2)}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">总交易量</span>
              <span className="text-lg font-bold text-gray-900">{formatCurrency(user.trading_stats.total_volume)}</span>
            </div>
          </div>
        </div>

        {/* Operator Info */}
        {user.operator && (
          <div className="card">
            <div className="card-header py-3">
              <h2 className="text-base font-semibold">运营商信息</h2>
            </div>
            <div className="card-body py-3 space-y-2">
              <div>
                <label className="text-xs font-medium text-gray-600">运营商名称</label>
                <p className="text-sm text-gray-900 mt-0.5">{user.operator.name}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">运营商代码</label>
                <p className="text-sm text-gray-900 mt-0.5">{user.operator.operator_code}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">佣金比例</label>
                <p className="text-sm text-gray-900 mt-0.5">{(user.operator.commission_rate * 100).toFixed(2)}%</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Time Info */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="card-title">时间信息</h2>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-600">注册时间</label>
            <p className="text-gray-900 mt-1">{formatDate(user.user.created_at)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">最后更新</label>
            <p className="text-gray-900 mt-1">{formatDate(user.user.updated_at)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">最后登录</label>
            <p className="text-gray-900 mt-1">
              {user.user.last_login_at ? formatDate(user.user.last_login_at) : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      {user.recent_activities && user.recent_activities.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">最近活动</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">类型</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">金额</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">描述</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">时间</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {user.recent_activities.map((activity) => (
                  <tr key={activity.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{activity.type}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {formatCurrency(activity.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{activity.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(activity.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
